import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type ReissueCertificateBody = {
  email: string;
};

/**
 * 車両証明書再発行指示API
 */
export const useReissueCertificateAPI = (): {
  state: APIState;
  postReissueCertificate: (
    vehicleId: string,
    body: ReissueCertificateBody,
  ) => Promise<boolean>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const postReissueCertificateAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      body: ReissueCertificateBody,
    ): Promise<Result<boolean, AxiosResponse>> => {
      try {
        await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/reissue_certificate`,
          body,
        );
        return new Success(true);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const postReissueCertificate = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, body: ReissueCertificateBody) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.post_reissue_certificate', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return false;
        }
        setState('loading');
        const res = await postReissueCertificateAPI(
          project!.id,
          environment!.environment_id,
          vehicleId,
          body,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.post_reissue_certificate', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return false;
        }
        notifySuccess({
          message: t('api.fms.post_reissue_certificate', {
            status: 'successfully',
          }),
          testIdAttrs: {
            wrapperTestId:
              'vehicle_certificate_reissue_snackbar_success-status-div',
            closeButtonTestId:
              'vehicle_certificate_reissue_snackbar_success-click-close_button',
          },
        });
        setState('hasValue');
        return true;
      },
      [
        notifyError,
        notifySuccess,
        postReissueCertificateAPI,
        t,
        getErrorMessage,
      ],
    ),
  );

  return {
    state,
    postReissueCertificate,
  };
};
